import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import PrivateRoutes from "./PrivateRoutes";
import Header from "../components/HeaderComponent";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { LayoutOnboarding } from "../pages/onBoarding/LayoutOnboarding";
import { OnBoardingUsername } from "../pages/onBoarding/OnBoardingUsername";
import { SelectDetails } from "../pages/onBoarding/SelectDetails";
import { OnboardingAge } from "../pages/onBoarding/OnboardingAge";
import { OnboardingUnvaccinated } from "../pages/onBoarding/OnboardingUnvaccinated";
import { Location } from "../pages/onBoarding/Location";
import { Description } from "../pages/onBoarding/Description";
import { ProfilePicture } from "../pages/onBoarding/ProfilePicture";
import { OnboardingLivenessScreen } from "../pages/onBoarding/OnboardingLivenessScreen";
import { OnboardingBirth } from "../pages/onBoarding/OnboardingBirth";
import { AddImage } from "../pages/onBoarding/AddImage";
import { onGetProfileConfig } from "../store/OnboardingThunk";
import CircularProgress from "../../../../core/components/CircularProgress";
import { chatSocketLifecycleChannel } from "../../../chat/data/datasource/ChatSocketDataSource";
import { ProfileScreen } from "../pages/profile/ProfileScreen";
import { SettingsScreen } from "../../../settings/presentation/pages/SettingsScreen";
import { VerificationScreen } from "../../../settings/presentation/pages/VerificationScreen";
import { VerificationCodeScreen } from "../../../settings/presentation/pages/VerificationCodeScreen";
import { VerificationLivenessScreen } from "../../../settings/presentation/pages/VerificationLivenessScreen";
import { ReportIssueScreen } from "../../../settings/presentation/pages/ReportIssueScreen";
import { DeleteAccountScreen } from "../../../settings/presentation/pages/DeleteAccountScreen";
import { SearchLocationScreen } from "../../../settings/presentation/pages/SearchLocation";
import { AffiliateScreen } from "../../../settings/presentation/pages/AffiliateScreen";
import { AffiliateOnlyDashboardScreen } from "../../../settings/presentation/pages/AffiliateOnlyDashboardScreen";
import { TutorialScreen } from "../../../settings/presentation/pages/TutorialScreen";
import { TutorialVideoScreen } from "../../../settings/presentation/pages/TutorialVideoScreen";
import { MessagesScreen } from "../../../chat/presentation/pages/MessagesScreen";
import { NotificationScreen } from "../../../notification/presentation/pages/NotificationScreen";
import { MatchScreen } from "../../../match/presentation/pages/MatchScreen";
// import { ModalWelcome } from "../components/ModalWelcome";
import { onGetChats } from "../../../chat/presentation/store/ChatThunk";
import { onGetPendingPartner } from "../../../settings/presentation/store/PartnerThunk";
import { ToastNotification } from "../../../notification/presentation/pages/ToastNotification";
import { PurchaseSuccessful } from "../../../../core/components/PurchaseSuccessful";
import { SubscriptionScreen } from "../../../subscriptions/presentation/pages/SubscriptionScreen";
import { CommunityHelpBoardScreen } from "../../../community/presentation/pages/CommunityHelpBoardScreen";
import { PurchaseCancelled } from "../../../subscriptions/presentation/pages/PurchaseCancelled";
import { PushNotificationModal } from "../components/PushNotificationModal";
import { useLocationShared } from "../../../../core/hooks/useLocationShared";
import { InstantMessageInfoScreen } from "../pages/profile/InstantMessageInfoScreen";
import { RelationshipPrioritiesScreen } from "../../../relationship-priorities/presentation/RelationshipPrioritiesScreen";
import { useActivateNotifications } from "../../../settings/presentation/hook/useActivateNotifications";
import { changePushState } from "../store/OnboardingSlice";
import { getFirstIncompleteOnboardingRoute, hasCompleteCoreOnboardingProfile, hasExistingOnboardingProgress } from "./onboardingResume";
import { SafeRenderBoundary } from "../../../../core/components/SafeRenderBoundary";
import {
	loadInitialAppData,
	loadSignedInBootstrapData,
	refreshVisibleRealtimeData,
	startRealtimeSession,
	stopRealtimeSession,
} from "./realtimeSession";
import { getRealtimeIdleDelay } from "./realtimeActivity";

const App: React.FC = () => {
  const { user, userLoading, error, getProfileConfig } = useSelector((state: RootState) => state.onboarding);
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const realtimeConnectedRef = useRef(false);
  const realtimeGenerationRef = useRef(0);
  const realtimeIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRealtimeActivityAtRef = useRef(Date.now());
  const signedInBootstrapLoadedRef = useRef(false);
  const shouldResumeIncompleteOnboarding =
    (user?.status_onboarding === 0 || !hasCompleteCoreOnboardingProfile(user)) &&
    hasExistingOnboardingProgress(user);
  const resumeOnboardingRoute = getFirstIncompleteOnboardingRoute({
    user,
    profileConfig: getProfileConfig,
  });

  useEffect(() => {
    if (shouldResumeIncompleteOnboarding) {
      dispatch(onGetProfileConfig());
    }
  }, [dispatch, shouldResumeIncompleteOnboarding]);

  const startRealtime = useCallback(() => {
    if (realtimeConnectedRef.current) return;
    realtimeConnectedRef.current = true;
    const generation = ++realtimeGenerationRef.current;
    void startRealtimeSession(dispatch).then(() => {
      if (
        !realtimeConnectedRef.current ||
        realtimeGenerationRef.current !== generation
      ) {
        stopRealtimeSession(dispatch);
      }
    });
  }, [dispatch]);

  const stopRealtime = useCallback(() => {
    if (!realtimeConnectedRef.current) return;
    realtimeConnectedRef.current = false;
    realtimeGenerationRef.current += 1;
    stopRealtimeSession(dispatch);
  }, [dispatch]);

  const clearRealtimeIdleTimer = useCallback(() => {
    if (realtimeIdleTimerRef.current !== null) {
      clearTimeout(realtimeIdleTimerRef.current);
      realtimeIdleTimerRef.current = null;
    }
  }, []);

  const scheduleRealtimeIdleStop = useCallback(() => {
    clearRealtimeIdleTimer();
    if (document.visibilityState !== "visible") return;

    const delay = getRealtimeIdleDelay({
      pathname: window.location.pathname,
      now: Date.now(),
      lastActivityAt: lastRealtimeActivityAtRef.current,
    });
    if (delay === null) return;

    realtimeIdleTimerRef.current = setTimeout(() => {
      const remainingDelay = getRealtimeIdleDelay({
        pathname: window.location.pathname,
        now: Date.now(),
        lastActivityAt: lastRealtimeActivityAtRef.current,
      });
      if (remainingDelay === null) return;
      if (remainingDelay > 0) {
        scheduleRealtimeIdleStop();
        return;
      }
      stopRealtime();
    }, delay);
  }, [clearRealtimeIdleTimer, stopRealtime]);

  const recordRealtimeActivity = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    lastRealtimeActivityAtRef.current = Date.now();
    startRealtime();
    scheduleRealtimeIdleStop();
  }, [scheduleRealtimeIdleStop, startRealtime]);

  useEffect(() => {
    loadInitialAppData(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (user?.status_onboarding !== 1) {
      signedInBootstrapLoadedRef.current = false;
      stopRealtime();
      return;
    }

    if (!signedInBootstrapLoadedRef.current) {
      signedInBootstrapLoadedRef.current = true;
      loadSignedInBootstrapData(dispatch);
    }

    if (document.visibilityState === "visible") {
      lastRealtimeActivityAtRef.current = Date.now();
      startRealtime();
      scheduleRealtimeIdleStop();
    }

    return () => {
      clearRealtimeIdleTimer();
      stopRealtime();
    };
  }, [
    clearRealtimeIdleTimer,
    dispatch,
    scheduleRealtimeIdleStop,
    startRealtime,
    stopRealtime,
    user?.status_onboarding,
  ]);

  useEffect(() => {
    if (user?.status_onboarding !== 1) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        recordRealtimeActivity();
        refreshVisibleRealtimeData(dispatch);
        return;
      }

      clearRealtimeIdleTimer();
      stopRealtime();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    clearRealtimeIdleTimer,
    dispatch,
    recordRealtimeActivity,
    stopRealtime,
    user?.status_onboarding,
  ]);

  useEffect(() => {
    if (user?.status_onboarding !== 1) return;

    const activityEvents: Array<keyof WindowEventMap> = [
      "focus",
      "keydown",
      "pointerdown",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, recordRealtimeActivity, { passive: true });
    });
    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordRealtimeActivity);
      });
    };
  }, [recordRealtimeActivity, user?.status_onboarding]);

  useEffect(() => {
    if (user?.status_onboarding !== 1) return;
    recordRealtimeActivity();
  }, [location.pathname, recordRealtimeActivity, user?.status_onboarding]);

  useEffect(() => {
    if (user?.status_onboarding !== 1) {
      return;
    }

    const handleChatReconnect = () => {
      refreshVisibleRealtimeData(dispatch);
    };

    chatSocketLifecycleChannel.addEventListener("reconnect", handleChatReconnect);
    return () => {
      chatSocketLifecycleChannel.removeEventListener("reconnect", handleChatReconnect);
    };
  }, [dispatch, user?.status_onboarding]);
  // A direct link or browser refresh can render once before the signed-in
  // member profile request starts. Keep the requested route in place until
  // that first request finishes instead of briefly treating the member as an
  // incomplete profile and redirecting them to Discover.
  // Returning to the app refreshes profile configuration. Keep the already
  // loaded profile editor mounted so that refresh cannot discard unsaved text.
  // Initial loading, incomplete accounts and every other route keep their gate.
  const keepLoadedProfileMounted = location.pathname === "/profile" &&
    user?.status_onboarding === 1 && hasCompleteCoreOnboardingProfile(user) &&
    Boolean(getProfileConfig);
  if ((userLoading && !keepLoadedProfileMounted) || (!user && !error)) {
    return (
      <div className="h-svh flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (
    user?.status_onboarding === 0 ||
    user?.status_onboarding === undefined ||
    !hasCompleteCoreOnboardingProfile(user)
  ) {
    return (
      <Routes>
        <Route element={<LayoutOnboarding />}>
          <Route
            path="/"
            element={
              resumeOnboardingRoute === "/" ? (
                <OnboardingAge />
              ) : (
                <Navigate to={resumeOnboardingRoute} replace />
              )
            }
          />
          <Route path="/unvaccinated" element={<OnboardingUnvaccinated />} />
          <Route path="/username" element={<OnBoardingUsername />} />
          <Route path="birth" element={<OnboardingBirth />} />
          <Route path="selectdetails" element={<SelectDetails />} />
          <Route path="location" element={<Location />} />
          <Route path="profilepicture" element={<ProfilePicture />} />
          <Route path="profilepicture/liveness" element={<OnboardingLivenessScreen />} />
          <Route path="addimage" element={<AddImage />} />
          <Route path="description" element={<Description />} />
        </Route>
        <Route path="/*" element={<Navigate to={resumeOnboardingRoute} replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route element={<PrivateRoutes />}>
        <Route path="/purchase-confirmed" element={<PurchaseSuccessful />} />
        <Route path="/purchase-cancelled" element={<PurchaseCancelled />} />
        <Route element={<Layout />}>
          <Route
            path="/"
            element={
              <SafeRenderBoundary
                storageKey="HOME_RENDER_ERROR"
                fallback={<Navigate to="/profile" replace />}
              >
                <MatchScreen />
              </SafeRenderBoundary>
            }
          />
          <Route path="profile" element={<ProfileScreen />} />
          <Route path="/profile/relationship-priorities" element={<RelationshipPrioritiesScreen />} />
          <Route path="/profile/instant-message-info" element={<InstantMessageInfoScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/settings/report-issue" element={<ReportIssueScreen />} />
          <Route path="/affiliate" element={<AffiliateScreen />} />
          <Route path="/affiliate/:section" element={<AffiliateScreen />} />
          <Route path="/affiliate-dashboard" element={<AffiliateOnlyDashboardScreen />} />
          <Route path="/affiliate-dashboard/:section" element={<AffiliateOnlyDashboardScreen />} />
          <Route path="/settings/affiliate" element={<AffiliateScreen />} />
          <Route path="/settings/affiliate/:section" element={<AffiliateScreen />} />
          <Route path="/settings/delete-account" element={<DeleteAccountScreen />} />
          <Route path="/settings/location" element={<SearchLocationScreen />} />
          <Route path="/settings/tutorial" element={<TutorialScreen />} />
          <Route path="/settings/tutorial/:videoId" element={<TutorialVideoScreen />} />
          <Route path="/community" element={<CommunityHelpBoardScreen />} />
          <Route path="/settings/statusVerification" element={<VerificationScreen />} />
          <Route path="/settings/statusVerification/code" element={<VerificationCodeScreen />} />
          <Route path="/settings/statusVerification/liveness" element={<VerificationLivenessScreen />} />
          <Route
            path="/live-face-scan"
            element={<Navigate to="/settings/statusVerification/liveness" replace />}
          />
          <Route path="messages" element={<MessagesScreen />} />
          <Route path="messages/:chatId" element={<MessagesScreen />} />
          <Route path="notification" element={<NotificationScreen fullPage />} />
          <Route path="subscription" element={<SubscriptionScreen />} />
        </Route>
      </Route>
      <Route path="/*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const Layout: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.onboarding);
  const location = useLocation();
  const activateNotifications = useActivateNotifications();
  const [isModalPushNotification, setIsModalPushNotification] = useState(() => {
    const notificationPermission =
      typeof Notification !== "undefined" ? Notification.permission : "default";
    const shouldPromptOnFirstVisit = notificationPermission === "default";

    return shouldPromptOnFirstVisit;
  });
  const [timeOutOfView, setTimeOutOfView] = useState<number>(0);
  const isNotification = location.pathname === "/notification";
  const isMemberAffiliatePage =
    location.pathname === "/affiliate" ||
    location.pathname.startsWith("/affiliate/") ||
    location.pathname === "/settings/affiliate" ||
    location.pathname.startsWith("/settings/affiliate/");
  useLocationShared();

  useEffect(() => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
      return;
    }

    if (!user?.id || user.status_onboarding !== 1) {
      return;
    }

    dispatch(changePushState("enabled"));
    void activateNotifications({ requestPermission: false });
  }, [activateNotifications, dispatch, user?.id, user?.status_onboarding]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeOutOfView >= 10) {
          dispatch(onGetChats());
          dispatch(onGetPendingPartner());
        }
        setTimeOutOfView(0); 
      } else {
        const timer = setInterval(() => {
          if (timeOutOfView < 10) setTimeOutOfView((prevTime) => prevTime + 1);
          if(document.visibilityState === 'visible') {
            clearInterval(timer);
            setTimeOutOfView(0);
          }
        }, 1000);
        return () => clearInterval(timer);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, timeOutOfView]);

  const shellClassName = isMemberAffiliatePage
    ? "relative flex max-md:flex-col bg-white max-md:overflow-y-hidden max-md:min-h-svh max-md:max-h-svh md:min-h-screen md:h-screen"
    : "relative flex max-md:flex-col bg-gradient-with-image max-md:overflow-y-hidden max-md:min-h-svh max-md:max-h-svh md:min-h-screen md:h-screen md:p-8 md:pb-0";
  const mainClassName = isMemberAffiliatePage
    ? "affiliate-member-scroll-frame flex flex-col md:z-10 w-full bg-white min-h-0 overflow-x-hidden overflow-y-auto flex-grow max-md:h-[91svh] max-md:max-h-[91svh]"
    : `flex flex-col md:z-10 w-full bg-grayDefault min-h-full md:min-h-0 ${isNotification ? "md:overflow-hidden" : "md:overflow-y-auto"} md:p-6 flex-grow min-md:min-h-[91svh] max-md:max-h-[91svh] max-md:h-[91svh]`;

  return (
    <div className={shellClassName}>
      <SafeRenderBoundary fallback={null} storageKey="PUSH_MODAL_RENDER_ERROR">
        <PushNotificationModal isModalPushNotification={isModalPushNotification} setIsModalPushNotification={setIsModalPushNotification} />
      </SafeRenderBoundary>
      <SafeRenderBoundary fallback={null} storageKey="HEADER_RENDER_ERROR">
        <Header isNotification={isNotification} />
      </SafeRenderBoundary>
      <SafeRenderBoundary fallback={null} storageKey="TOAST_RENDER_ERROR">
        <ToastNotification isNotification={isNotification} />
      </SafeRenderBoundary>

      <div className="absolute max-md:hidden top-0 left-0 w-full bg-primary h-32" />
      <main className={mainClassName}>
        <SafeRenderBoundary fallback={<Navigate to="/profile" replace />} storageKey="MAIN_RENDER_ERROR">
          <Outlet />
        </SafeRenderBoundary>
      </main>
    </div>
  );
};


export default App;
